import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { FieldArray, submit } from 'redux-form';
import { Row, Col, CardBody, Card, UncontrolledTooltip } from 'reactstrap';
import Sector from '../../models/Sector';
import { Utils } from '../../services';
import SelectField from '../SelectField/SelectField';

import './SectorsField.css';

const SectorsField = ({ sectors, name, single, preventSubmit, ..._rest }) => {
    const dispatch    = useDispatch();
    const { t, i18n } = useTranslation();
    const ui_lang = Utils.i18nUiLang(i18n);

    if (single) {
        const options = (sectors || []).map((sector) => ({
            key: sector.id,
            text: Utils.localized_or_fallback(sector.name, ui_lang),
            value: sector.id,
            className: Utils.tree_depth_class(sector.depth),
        }));

        return <SelectField name={ name } options={ options } clearable />;
    }

    const Component = ({ fields, meta: { form }}) => {

        const selectedSectors = React.useMemo(() => fields.getAll() || [], [fields]);


        const availableSectors = React.useMemo(() => {
            const selectedIds = selectedSectors.map(s => s.id);
            const available   = sectors?.filter(s => !selectedIds.includes(s.id));

            return Sector.buildTree(available, ui_lang);
            // Outer-scope `sectors` / `ui_lang`: parent re-renders recreate this Component.
            // eslint-disable-next-line react-hooks/exhaustive-deps -- nested FieldArray component
        }, [selectedSectors]);

        const removeSector = (index) => {
            fields.remove(index);
            !preventSubmit && setTimeout(() => dispatch(submit(form)), 10);
        };

        const addSector = (sector) => {
            if (sector.addedForTree)
                return false;
            fields.push(sector);
            !preventSubmit && setTimeout(() => dispatch(submit(form)), 10);
        };

        const stripHtml = (html) => String(html || '').replace(/<\/?[^>]+(>|$)/g, '');
        const sectorName = (s) => Utils.localized_or_fallback(s?.name, ui_lang);
        const sectorDescription = (s) => Utils.localized_or_fallback(s?.description, ui_lang, '');

        return (
            <div className="SectorsField">
                <Row>
                    <Col sm={12} md={5}>
                        <Card className="border mb-3 h-100">
                            <CardBody>
                                <table className='w-100'>
                                    <thead>
                                        <tr>
                                            <th className='text-center'>{t("sectors.available")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {availableSectors?.length > 0 && availableSectors.map((s) => (
                                            <tr key={s.id} className={`text-center add ${s.addedForTree ? 'addedForTree' : ''}`} onClick={(_e) => addSector(s)} >
                                                <td>
                                                    <span className={Utils.tree_depth_class(s.depth)}>{sectorName(s)} <i className='fa fa-long-arrow-right '></i></span>
                                                    { sectorDescription(s)?.length > 0 &&
                                                        <span id={`hover-availableSector-${s.id}`} className='mr-3' >
                                                            <i className="fa fa-info-circle"></i>
                                                            <UncontrolledTooltip target={`hover-availableSector-${s.id}`}>
                                                                {stripHtml(sectorDescription(s))}
                                                            </UncontrolledTooltip>
                                                        </span>
                                                    }
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </CardBody>
                        </Card>
                    </Col>

                    <Col sm={12} md={2} className='text-center'>
                        <i className='fa fa-exchange fa-2x'></i>
                    </Col>

                    <Col sm={12} md={5}>
                        <Card className="border mb-3 h-100">
                            <CardBody>
                                <table className='w-100'>
                                    <thead>
                                        <tr>
                                            <th className='text-center'>{t("sectors.your_sectors")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedSectors.map((s, index) => (
                                            <tr key={s.id} className="text-center remove" onClick={(_e) => removeSector(index)} >
                                                <td>
                                                    <span className="text-bold pr-5 ">
                                                        #{ index + 1 }&nbsp;-&nbsp;
                                                    </span>
                                                    <i className='fa fa-long-arrow-left '></i>{sectorName(s)}
                                                    {sectorDescription(s)?.length > 0 &&
                                                        <span id={`hover-selectedSectors-${s.id}`} className='ml-3' >
                                                            <i className="fa fa-info-circle"></i>
                                                            <UncontrolledTooltip target={`hover-selectedSectors-${s.id}`}>
                                                                {stripHtml(sectorDescription(s))}
                                                            </UncontrolledTooltip>
                                                        </span>
                                                    }
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        );
    }

    return (
        <FieldArray name={ name } component={ Component } sectors={sectors || []} />
    );
}

export default SectorsField;
